import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import * as LucideIcons from "lucide-react";
import { Search, FileText } from "lucide-react";

const CATEGORIES = [
  { key: "documenti", label: "Documenti" },
  { key: "applicazioni", label: "Applicazioni" },
  { key: "marketing", label: "Marketing" },
  { key: "compliance", label: "Compliance" },
  { key: "dati", label: "Dati" },
  { key: "utility", label: "Utility" },
];

const DIFFICULTY_COLORS = {
  facile: { bg: "rgba(34,197,94,0.12)", color: "#15803d" },
  medio: { bg: "rgba(234,179,8,0.15)", color: "#a16207" },
  avanzato: { bg: "rgba(200,16,46,0.12)", color: "#C8102E" },
};

export default function Catalogo() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [params] = useSearchParams();
  const sector = params.get("sector") || "";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const p = {};
        if (activeCategory) p.category = activeCategory;
        if (search) p.q = search;
        if (sector) p.sector = sector;
        const { data } = await api.get("/recipes", { params: p });
        setRecipes(data.items || []);
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [search, activeCategory, sector]);

  const grouped = CATEGORIES.map((c) => ({
    ...c,
    items: recipes.filter((r) => r.category === c.key),
  })).filter((g) => g.items.length > 0);

  return (
    <div>
      <div className="adr-overline mb-3">Catalogo</div>
      <h1 className="serif text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A1A1A] mb-2">
        Ricette pronte all'uso
      </h1>
      <p className="text-[#555555] mb-8 max-w-2xl">
        Scegli una ricetta validata dalla redazione Confindustria e generala con pochi clic. Tutte le ricette sono in italiano.
        {sector && <span className="block mt-1 text-xs">Filtrate per reparto: <strong className="capitalize">{sector.replace("_", " ")}</strong> · <Link to="/catalogo" className="underline">rimuovi filtro</Link></span>}
      </p>

      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" />
          <input
            type="text"
            placeholder="Cerca per nome o descrizione..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="adr-input pl-9"
            data-testid="catalogo-search"
          />
        </div>
        <select
          value={activeCategory}
          onChange={(e) => setActiveCategory(e.target.value)}
          className="adr-input sm:w-56"
          data-testid="catalogo-category-filter"
        >
          <option value="">Tutte le categorie</option>
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="adr-skeleton h-44" />
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-16 text-[#555555]">
          <FileText className="w-12 h-12 mx-auto mb-3 text-[#0F3D6B]/40" />
          Nessuna ricetta trovata.
        </div>
      ) : (
        <div className="space-y-12">
          {grouped.map((g) => (
            <div key={g.key}>
              <h2 className="serif text-xl font-semibold text-[#1A1A1A] mb-4 flex items-baseline gap-3">
                {g.label}
                <span className="text-xs text-[#555555] font-sans font-normal">
                  {g.items.length} ricette
                </span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.items.map((r) => {
                  const Icon = LucideIcons[r.icon_name] || FileText;
                  const dc = DIFFICULTY_COLORS[r.difficulty] || DIFFICULTY_COLORS.facile;
                  return (
                    <Link
                      key={r.id}
                      to={`/catalogo/${r.slug}`}
                      className="adr-card p-5 block"
                      data-testid={`recipe-card-${r.slug}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div
                          className="w-10 h-10 rounded-md flex items-center justify-center"
                          style={{ background: "rgba(15,61,107,0.08)" }}
                        >
                          <Icon className="w-5 h-5" style={{ color: "#0F3D6B" }} strokeWidth={1.6} />
                        </div>
                        <span
                          className="adr-badge capitalize"
                          style={{ background: dc.bg, color: dc.color }}
                        >
                          {r.difficulty}
                        </span>
                      </div>
                      <h3 className="serif text-lg font-semibold text-[#1A1A1A] mb-1.5 leading-snug">
                        {r.name_it}
                      </h3>
                      <p className="text-sm text-[#555555] leading-snug line-clamp-3">
                        {r.description_it}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
